function getUserDisplayName(user) {
  if (!user) return "Unknown user";
  return user.displayName || user.name || user.fullName || user.email || user.uid || "Unknown user";
}

export function normalizeTimestamp(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function getRevisionHistory(form) {
  return Array.isArray(form?.revisionHistory) ? form.revisionHistory : [];
}

export function getCurrentRevision(form) {
  if (Number.isFinite(form?.documentControl?.currentRevision)) {
    return form.documentControl.currentRevision;
  }
  const history = getRevisionHistory(form);
  const maxRevision = history.reduce((max, entry) => {
    const revision = Number(entry?.revisionNumber || 0);
    return revision > max ? revision : max;
  }, 0);
  return maxRevision || 1;
}

export function canEditOnMobile(form) {
  const isMobileCreated =
    form?.createdVia === "mobile" || form?.source === "mobile" || form?.allowMobileEdit === true;
  const wasEditedOnWeb = form?.lastEditedVia === "web";
  return isMobileCreated && !wasEditedOnWeb;
}

export function getMobileEditLabel(form) {
  return canEditOnMobile(form) ? "Web + mobile" : "Web only";
}

export function getDocumentControlSummary(form) {
  const control = form?.documentControl || {};
  const revision = getCurrentRevision(form);
  const history = getRevisionHistory(form);
  const latestEntry = history.length ? history[history.length - 1] : null;

  return {
    documentType: control.documentType || "Inspection Form",
    authorName: control.authorName || getUserDisplayName({ name: form?.createdByName, email: form?.createdBy }),
    revisionNumber: revision,
    revisionLabel: `Rev ${revision}`,
    approvedAt: normalizeTimestamp(control.approvedAt || form?.updatedAt || form?.createdAt),
    approvedByName: control.approvedByName || latestEntry?.changedByName || "Unknown user",
    lastChangeSummary: control.lastChangeSummary || latestEntry?.changeSummary || "Initial issue",
    lastComments: control.lastComments || latestEntry?.comments || "",
    mobileEditLabel: getMobileEditLabel(form),
    latestEntry,
    history,
  };
}

function buildHistoryEntry({ revisionNumber, user, platform, summary, comments }) {
  return {
    revisionNumber,
    revisionLabel: `Rev ${revisionNumber}`,
    changedByUid: user?.uid || "unknown",
    changedByName: getUserDisplayName(user),
    changeSummary: summary || (revisionNumber === 1 ? "Initial issue" : "Updated form"),
    comments: comments || "",
    platform,
    updatedAt: new Date().toISOString(),
  };
}

export function buildNewDocumentControl({ user, platform, summary, comments }) {
  const historyEntry = buildHistoryEntry({
    revisionNumber: 1,
    user,
    platform,
    summary,
    comments,
  });

  return {
    documentControl: {
      documentType: "Inspection Form",
      authorUid: user?.uid || "unknown",
      authorName: getUserDisplayName(user),
      currentRevision: 1,
      approvedAt: historyEntry.updatedAt,
      approvedByUid: historyEntry.changedByUid,
      approvedByName: historyEntry.changedByName,
      lastChangeSummary: historyEntry.changeSummary,
      lastComments: historyEntry.comments,
      lastEditedVia: platform,
    },
    revisionHistory: [historyEntry],
  };
}

export function buildUpdatedDocumentControl({ existingForm, user, platform, summary, comments }) {
  const revisionNumber = getCurrentRevision(existingForm) + 1;
  const history = getRevisionHistory(existingForm).slice();
  const historyEntry = buildHistoryEntry({
    revisionNumber,
    user,
    platform,
    summary,
    comments,
  });

  history.push(historyEntry);

  return {
    documentControl: {
      ...(existingForm?.documentControl || {}),
      documentType: existingForm?.documentControl?.documentType || "Inspection Form",
      authorUid: existingForm?.documentControl?.authorUid || existingForm?.createdBy || user?.uid || "unknown",
      authorName:
        existingForm?.documentControl?.authorName ||
        existingForm?.createdByName ||
        getUserDisplayName(user),
      currentRevision: revisionNumber,
      approvedAt: historyEntry.updatedAt,
      approvedByUid: historyEntry.changedByUid,
      approvedByName: historyEntry.changedByName,
      lastChangeSummary: historyEntry.changeSummary,
      lastComments: historyEntry.comments,
      lastEditedVia: platform,
    },
    revisionHistory: history,
  };
}
